//-- Copyright (c) 2021 TMT Insights Group, LLC
import Modal from '@/components/_common/Modal/Modal';
import styled from '@emotion/styled';
import { Box, Button, Grid, TextField, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import TimecodeInput, { formatSecondsToTimecode, parseTimecodeToSeconds } from './TimecodeInput';
import { CustomMarker } from './video-player.types';
import { nanoid } from 'nanoid';

export type Marker = {
  id?: string;
  name?: string;
  time?: string; // formatted timecode
  duration?: string; // formatted duration
  color?: string; // hex
  comments?: string;
};

type MarkerEditorModalProps = {
  open: boolean;
  toggleOpen: () => void;
  data?: CustomMarker;
  triggerSource?: 'control' | 'in-keypress';
  onSave?: (m: CustomMarker) => void | Promise<void>;
  onDelete?: (id?: string) => void | Promise<void>;
};

const DEFAULT_COLORS = ['#3D9BFF', '#FF3D57', '#B12AFF', '#FFC107', '#D77AFF', '#FF8A65', '#4CAF50', '#E0E0E0'];

const MarkerEditorModal = ({ open, toggleOpen, data, triggerSource = 'control', onSave, onDelete }: MarkerEditorModalProps) => {
  const isInKeypressTrigger = triggerSource === 'in-keypress';
  const [name, setName] = useState<string>(data?.label || '');
  const [time, setTime] = useState<string | undefined>(formatSecondsToTimecode(data?.start || 0));
  const [duration, setDuration] = useState<string | undefined>(formatSecondsToTimecode(data?.end || 0));
  const [color, setColor] = useState<string>(data?.color || DEFAULT_COLORS[0]);
  const [comments, setComments] = useState<string>(data?.comments || '');

  useEffect(() => {
    if (!open) return;

    setName(data?.label || '');
    setTime(formatSecondsToTimecode(data?.start || 0));
    setDuration(formatSecondsToTimecode(data?.end || 0));
    setColor(data?.color || DEFAULT_COLORS[0]);
    setComments(data?.comments || '');
  }, [open, data]);

  const handleSave = async () => {
    if (isInKeypressTrigger) {
      const start = data?.start || 0;
      const end = data?.end ?? start;
      onSave?.({
        id: data?.id || nanoid(),
        url: data?.url || '',
        label: name,
        start,
        end,
        color,
        comments: data?.comments,
      } as CustomMarker);
      return;
    }

    const start = parseTimecodeToSeconds(time || '');
    const end = start + parseTimecodeToSeconds(duration || '');
    onSave?.({
      id: data?.id || nanoid(),
      url: data?.url || '',
      label: name,
      start,
      end,
      color,
      comments,
    } as CustomMarker);
  };

  const handleDelete = async () => {
    onDelete?.(data?.id);
  };

  return (
    <MarkerModal maxWidth={396} open={open} onClose={() => toggleOpen()} title={name || 'Marker Name'}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            label="Marker Name"
            fullWidth
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </Grid>
        {!isInKeypressTrigger && (
          <Grid item xs={12} sm={12}>
            <TimecodeInput label="Time" value={time} onChange={setTime} />
          </Grid>
        )}
        {!isInKeypressTrigger && (
          <Grid item xs={12} sm={12}>
            <TimecodeInput label="Duration" value={duration} onChange={setDuration} />
          </Grid>
        )}
      </Grid>

      <OptionsWrapper>
        {!isInKeypressTrigger && <Typography variant="subtitle1" sx={{ mb: 1 }}>Options:</Typography>}

        <Typography variant="body2" sx={{ mb: 1 }}>Marker Color</Typography>
        <ColorRow>
          {DEFAULT_COLORS.map((c) => (
            <ColorDot key={c} selected={c === color} onClick={() => setColor(c)} style={{ backgroundColor: c }} />
          ))}
        </ColorRow>

        {!isInKeypressTrigger && <Typography variant="body2" sx={{ mt: 2, mb: 1 }}>Comments</Typography>}
        {!isInKeypressTrigger && <TextField value={comments} onChange={(e) => setComments(e.target.value)} multiline rows={6} fullWidth />}

        <BottomActions container>
          {!isInKeypressTrigger && data?.id && (
            <Grid item>
              <DeleteButton variant="outlined" onClick={handleDelete}>Delete</DeleteButton>
            </Grid>
          )}
          <Grid item sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
            <Button color="default" onClick={toggleOpen}>Cancel</Button>
            <Button color="primary" variant="contained" onClick={handleSave}>Save</Button>
          </Grid>
        </BottomActions>
      </OptionsWrapper>
    </MarkerModal>
  );
};

export default MarkerEditorModal;

const MarkerModal = styled(Modal)`
  .MuiPaper-root > .MuiBox-root {
    max-height: none;
    position: relative;
  }

  .modal-body {
    overflow: unset;
  }
`;

const OptionsWrapper = styled(Box)`
  margin-top: 8px;
`;

const ColorRow = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  margin-bottom: 12px;
`;

const ColorDot = styled.div<{ selected?: boolean }>`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  cursor: pointer;
  box-shadow: ${({ selected }) => (selected ? '0 0 0 3px rgba(0,0,0,0.2) inset' : 'none')};
  border: ${({ selected }) => (selected ? '2px solid rgba(255,255,255,0.6)' : '2px solid transparent')};
`;

const BottomActions = styled(Grid)`
  margin-top: 16px;
  align-items: center;
`;

const DeleteButton = styled(Button)`
  color: ${({ theme }) => theme.palette.error.main};
  border-color: ${({ theme }) => theme.palette.error.main};
`;
